import { EventEmitter, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { OrderIdsModel } from '@models/orderIds-model';
import { plainToClass } from 'class-transformer';
import { BehaviorSubject } from 'rxjs';
import { CustomerLocationCheckInModalComponent } from '../_modals/customer-location-checkin/customer-location-checkin.component';
import { CustomerModel, CustomerOrderRelevantModel, CustomerType } from '../_models/customer';
import { BaseService } from './base.service';
import { CustomerDataService } from './customer-data.service';
import { DataApiService } from './data-api.service';
import { EnduserDisplayService } from './enduserdisplay.service';
import { EventsService } from './events.service';
import { HttpService } from './http.service';
import { IOrderOnBehalfService } from './interfaces/order-on-behalf.service.interface';
import { LocationCheckInService } from './location-checkin.service';
import { ModalService } from './modal.service';
import { OrdersDataService } from './orders-data.service';
import { SessionService } from './session.service';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root'
})
export class OrderOnBehalfService extends BaseService implements IOrderOnBehalfService {

	private readonly sessionKey = "currentCustomer";

	emitter: EventEmitter<CustomerModel>;

	private customerSubject: BehaviorSubject<CustomerModel | null>;

	public constructor(
		private modalService: ModalService,
		private userService: UserService,
		private sessionService: SessionService,
		private enduserDisplayService: EnduserDisplayService,
		private eventService: EventsService,
		private locationCheckInService: LocationCheckInService,
		private ordersDataService: OrdersDataService,
		private customerDataService: CustomerDataService,
		private httpService: HttpService,
		private dataApiService: DataApiService,
	) {
		super();
		this.customerSubject = new BehaviorSubject<CustomerModel | null>(plainToClass(CustomerModel, this.sessionService.get<CustomerModel>(this.sessionKey)));

		this.subscriptions.push(this.ordersDataService.orderDataChanged.subscribe(async () => {
			// we have to clear customerdata instantanious when backend order data changes.
			await this.clearOrderRelevantData();

			// after order data change, we reload data in background. If the reload is done, "updateOrderRelevantData" will not make and backend call. Thats why we wait.
			setTimeout(() => {
				this.updateOrderRelevantData();
			}, 5000);

		}));

		this.emitter = new EventEmitter<CustomerModel>();

		this.subscriptions.push(this.emitter.subscribe(async (x: CustomerModel) => {
			await this.setCustomer(x);
		}));
	}


	public customerAsObservable = () => this.customerSubject.asObservable();

	public customerSnapshot() {
		const customerSnapshotValue = this.customerSubject.getValue();
		return customerSnapshotValue;
	}

	public resetCustomer = () => this.customerSubject.next(null);

	public async setCustomer(customer: CustomerModel | null) {
		if (customer) {
			const emitter = new EventEmitter<boolean>();

			let customerType = customer.Type;

			let user = await this.userService.currentUser$.getValue();

			if (user?.IsDistributor) {
				customerType = CustomerType.OrderOnBehalf;
			}

			// ### when the emitter is eventually "nexted" we will hit this subscription

			emitter.subscribe(async x => {
				this.eventService.showLoader(true);
				let customerData: CustomerModel | null = null
				let token = null
				let street = null;
				let address = null;
				let countryCode = null;
				let shipToName = null;

				if (customer.IsNewPartner) {
					token = customer.Token;
					street = customer?.Street;
					address = customer?.Address;
					countryCode = customer.CountryCode;
					shipToName = customer?.ShipToName;
					token = await this.customerDataService.GetCustomerToken(customer);
				}
				else {
					customerData = await this.customerDataService.getCustomerByNo(customer.No);
					street = customerData?.Street
					address = customerData?.Address;
					countryCode = customerData?.CountryCode;
					shipToName = customerData?.ShipToName;
					token = customerData?.Token;
				}

				const customerModel = new CustomerModel(
					customer.No, 
					customerType, 
					customer.SalesDistrictType, 
					customer.Name, 
					street, 
					shipToName,
					customer.IsPlusPartner, 
					customer.IsPreciousPartner, 
					customer.IsBlocked, 
					customer.Email,
					customer.EmailAddresses, 
					customerData?.ShipToAddresses ?? [], 
					customerData?.ProductLinesPermissions ?? [],
					customerData?.Assortments ?? [], 
					token, 
					[], 
					[], 
					address, 
					customer.IsNewPartner, 
					false, 
					false, 
					customer.KeringEyewearBrands, 
					customer.Code, 
					countryCode, 
					customer.Status
				);

				await this.updateOrderRelevantData(customerModel);

				this.eventService.showLoader(false);
			});

			if (!customer.IsOrderDeeplinkMode) {
				this.triggerLocationCheckInModal(customer, emitter);
			}
			else {
				emitter.next(true);
			}
		}
		else {
			await this.setUserAndCustomerData(null, true);
			await this.updateOrderRelevantData(null);
		}
	}

	// This is used, because we had a wide spread problem in our codebase, where we use customer?.No or empty string.
	// We should never use empty string. User has a customerno aswell, and we should use that, which is why this method exits
	public async getCustomerOrUserNo() {
		let customer = this.customerSnapshot();
		let user = await this.userService.GetUser();

		if (customer || user) {
			return customer ? customer.No : user?.CustomerNo;
		}

		return undefined;
	}

	public async getIsNewPartner() {
		let customer = this.customerSnapshot();
		let user = await this.userService.GetUser();

		if (customer || user) {
			return customer ? customer.IsNewPartner : false;
		}

		return undefined;
	}

	public async getCurrentActiveOrderIds(): Promise<Array<OrderIdsModel> | undefined> {
		let customer = this.customerSnapshot();
		let user = await this.userService.GetUser();

		if (customer) {
			await this.updateOrderRelevantData(customer);
			customer = this.customerSnapshot();

			return customer?.OrderIds ?? [];
		}
		else {
			await this.updateOrderRelevantData();
			user = await this.userService.GetUser();
			if (user)
				return user.OrderIds;
		}

		return;
	}

	public async hasStartkitOnOrders(productLineCode?: string): Promise<boolean> {
		let customer = this.customerSnapshot();

		if (!customer?.StartkitsOnActiveOrders || customer.StartkitsOnActiveOrders.length === 0) {
			await this.updateOrderRelevantData();
			customer = this.customerSnapshot();
		}

		return customer?.StartkitsOnActiveOrders.some(sk => sk === productLineCode) ?? false;
	}

	public async clearOrderRelevantData() {
		const currentCustomer = this.customerSnapshot();

		if (!currentCustomer) {
			let currentUser = await this.userService.GetUser();
			if (currentUser)
				currentUser.OrderIds = [];
			this.userService.setUser(currentUser);
		}
		else {
			currentCustomer.StartkitsOnActiveOrders = [];
			currentCustomer.OrderIds = [];
			this.setUserAndCustomerData(currentCustomer, false);
		}
	}

	public async setUserAndCustomerData(customer: CustomerModel | null, customerChanged: boolean) {
		this.sessionService.set(this.sessionKey, customer);

		if (customerChanged) {
			this.customerSubject.next(customer);
		}

		let currentUser = await this.userService.GetUser();
		if (currentUser && currentUser.canOrderOnBehalfOfChainCustomer) { // Impersonation
			const user = await this.userService.getUserData();
			this.userService.setUser(user);
		}

		if (customer && !customer.isConsultantOrderOnBehalf) {
			this.enduserDisplayService.setConfigurationMode(true);
		}
	}

	private triggerLocationCheckInModal(customer: CustomerModel, emitter: EventEmitter<boolean>) {
		if (customer && customer.Type == CustomerType.OrderOnBehalf && !this.locationCheckInService.locationCheckInExistsForToday(customer)) {
			const modal = this.modalService.create<CustomerLocationCheckInModalComponent>(CustomerLocationCheckInModalComponent, { customer });

			const sub = modal.subscribe(ref => {
				if (ref.hostView.destroyed) {
					emitter.next(true);
					sub.unsubscribe();
				}
			});
		}
		else {
			// this.modalService.close(OrderOnBehalfModalComponent);
			emitter.next(true);
		}
	}

	private async getUserOrderRelevantData(userCustomerNo: string): Promise<CustomerOrderRelevantModel> {
		const orderOnUser = await this.dataApiService.getOrders(userCustomerNo); //await this.ordersDataService.getFromOrdersStore(userCustomerNo);
		let orderRelevantData: CustomerOrderRelevantModel;

		if (orderOnUser) {
			orderRelevantData = new CustomerOrderRelevantModel(orderOnUser);
		}
		else {
			const result: CustomerOrderRelevantModel = await this.httpService.getDataWithOfflineCheck<CustomerOrderRelevantModel>(`${environment.dataUrl}/Account/Userdata/OrderRelevantData`);
			orderRelevantData = plainToClass(CustomerOrderRelevantModel, result);
		}

		return orderRelevantData;
	}

	public async updateOrderRelevantData(newCustomer: CustomerModel | null = null) {
		const currentCustomer = newCustomer ? newCustomer : this.customerSnapshot();
		let currentUser = await this.userService.GetUser();

		if (currentUser) {
			if (!currentCustomer) {
				const userOrderData = await this.getUserOrderRelevantData(currentUser.CustomerNo);
				currentUser.OrderIds = userOrderData.OrderIds;
				this.userService.setUser(currentUser);
				currentUser.OrderingOnBehalfOfNewPartner = false;
			}
			else {
				if (currentCustomer?.IsNewPartner) {
					currentCustomer.ProductLinesPermissions = [];
					currentCustomer.Assortments = currentUser.Assortments;
					currentCustomer.StartkitsOnActiveOrders = [];
					currentUser.OrderingOnBehalfOfNewPartner = true;
				}
				else {
					currentUser.OrderingOnBehalfOfNewPartner = false;
				}

				const result: CustomerOrderRelevantModel = await this.httpService.getDataWithOfflineCheck<CustomerOrderRelevantModel>(`${environment.dataUrl}/Account/Customer/${currentCustomer.No}/OrderRelevantData`);

				const customerOrderData = plainToClass(CustomerOrderRelevantModel, result);

				currentCustomer.StartkitsOnActiveOrders = customerOrderData.StartkitsOnActiveOrders;
				currentCustomer.OrderIds = customerOrderData.OrderIds;
				this.setUserAndCustomerData(currentCustomer, newCustomer !== null);
			}
		}
	}

}
